import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import { useId } from "react";

import { isScoutWriteModeGql, type ScoutWriteModeGql } from "../hooks/scoutWriteMode";
import { useScoutWriteAccessControl } from "../hooks/useScoutWriteAccessControl";

export type ScoutWriteAccessToggleProps = {
  value: ScoutWriteModeGql;
  disabled: boolean;
  onChange: (next: ScoutWriteModeGql) => void;
  variant?: "inline" | "menu";
};

export function ScoutWriteAccessToggle({
  value,
  disabled,
  onChange,
  variant = "inline",
}: ScoutWriteAccessToggleProps) {
  const labelId = useId();
  const { isFeatureEnabled, controlLabel, infoTooltip, options } = useScoutWriteAccessControl({
    writeMode: value,
    onWriteModeChange: onChange,
    disabled,
  });

  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <Stack spacing={1} sx={variant === "menu" ? { px: 2, py: 1.5 } : { px: 2, py: 0.5 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <FormLabel id={labelId} sx={{ typography: "body2", color: "text.primary", m: 0 }}>
          {controlLabel}
        </FormLabel>
        <Tooltip title={infoTooltip}>
          <IconButton size="small" aria-label="More info about Scout write mode">
            <InfoOutlined fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </Stack>
      <FormControl disabled={disabled} fullWidth size="small">
        <Select
          value={value}
          disabled={disabled}
          labelId={labelId}
          displayEmpty
          onChange={(event) => {
            if (isScoutWriteModeGql(event.target.value)) {
              onChange(event.target.value);
            }
          }}
          MenuProps={{ disablePortal: true }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}
